import React, { useState, useEffect, useCallback } from "react";
import {
 Row,
 Col,
 Card,
 Table,
 Badge,
 Button,
 Modal
} from "react-bootstrap";
import api, { getFileUrl } from "../../../../api";
import swal from "sweetalert";

const STATUS_COLORS = {
 Approved: "success",
 "Pending Approval": "warning",
 Submitted: "info",
 Rejected: "danger",
};

const DocumentsTab = ({ workOrderId, filterType }) => {
 const [documents, setDocuments] = useState([]);
 const [loading, setLoading] = useState(true);
 const [selectedDoc, setSelectedDoc] = useState(null);
 const [showModal, setShowModal] = useState(false);

 // ==========================
 // FETCH DOCUMENTS FOR WO
 // ==========================
 const fetchData = useCallback(async () => {
 try {
 setLoading(true);
 const params = { workOrder: workOrderId, limit: 1000 };
 if (filterType) params.documentType = filterType; // Filter by document type name

 const res = await api.get("/documents", { params });
 if (res.data.success) {
 setDocuments(res.data.data || []);
 }
 } catch (err) {
 console.error("Failed to load documents:", err);
 } finally {
 setLoading(false);
 }
 }, [workOrderId, filterType]);

 useEffect(() => {
 if (workOrderId) fetchData();
 }, [workOrderId, filterType, fetchData]);

 // ==========================
 // PREVIEW FILE
 // ==========================
 const handlePreview = (url) => {
 if (!url) return;
 window.open(getFileUrl(url), "_blank");
 };
// ==========================
// DELETE DOCUMENT
// ==========================
const handleDelete = async (doc) => {
 const confirm = await swal({
 title: "Delete Document?",
 text: "This action cannot be undone.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/documents/${doc._id}`);

 if (res.data.success) {
 setDocuments((prev) => prev.filter((d) => d._id !== doc._id));

 swal("Deleted!", "Document removed successfully.", "success");
 } else {
 swal("Error", "Failed to delete document", "error");
 }
 } catch (err) {
 console.error("Delete error:", err);
 swal("Error", "Failed to delete document", "error");
 }
};
 // ==========================
 // DOWNLOAD FILE
 // ==========================
 const handleDownload = async (doc) => {
 try {
 const fileUrl = getFileUrl(doc.fileUrl);

 const link = document.createElement("a");
 link.href = fileUrl;
 link.download = doc.fileName || "document.pdf";
 link.click();
 } catch (err) {
 alert("Failed to download");
 }
 };

 // ==========================
 // VIEW DETAILS
 // ==========================
 const handleView = async (doc) => {
 try {
 const res = await api.get(`/documents/${doc._id}`);
 if (res.data.success) {
 setSelectedDoc(res.data.data);
 setShowModal(true);
 }
 } catch {
 alert("Failed to fetch details");
 }
 };

 const formatDate = (date) => {
 if (!date) return "—";
 return new Date(date).toLocaleDateString("en-IN");
 };

 if (loading) return <p>Loading document data...</p>;

 return (
 <>
 <Row className="g-4">
 {/* SUMMARY */}
 <Col md={4}>
 <Card className="shadow-sm border-0 rounded-3">
 <Card.Body>
 <h5 className="fw-bold mb-3">Document Summary</h5>
 <div>Total: <strong>{documents.length}</strong></div>
 <div className="text-success">Approved: <strong>{documents.filter(d => d.status === "Approved").length}</strong></div>
 <div className="text-warning">Pending Approval: <strong>{documents.filter(d => d.status === "Pending Approval").length}</strong></div>
 <div className="text-info">Submitted: <strong>{documents.filter(d => d.status === "Submitted").length}</strong></div>
 <div className="text-danger">Rejected: <strong>{documents.filter(d => d.status === "Rejected").length}</strong></div>
 </Card.Body>
 </Card>
 </Col>

 {/* LIST */}
 <Col md={12}>
 <Card className="shadow-sm border-0 rounded-3">
 <Card.Body>
 <h5 className="fw-bold mb-3">Documents</h5>

 {documents.length === 0 ? (
 <div className="text-center text-muted py-3">
 No documents found for this work order.
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover>
 <thead>
 <tr>
 <th>#</th>
 <th>Type</th>
 <th>File</th>
 <th>Status</th>
 <th>Version</th>
 <th>Date</th>
 <th style={{ width: 180 }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {documents.map((doc, i) => (
 <tr key={doc._id}>
 <td>{i + 1}</td>
 <td>{doc.documentType?.name || "N/A"}</td>
 <td>{doc.fileName}</td>
 <td>
 <Badge className="rounded-pill" bg={STATUS_COLORS[doc.status] || "secondary"}>
 {doc.status}
 </Badge>
 </td>
 <td>v{doc.version}</td>
 <td>{formatDate(doc.submissionDate)}</td>

 <td className="text-nowrap">
 <Button
 size="sm"
 variant="outline-primary"
 className="me-1 border-0"
 onClick={() => handleView(doc)}
 title="View Details"
 >
 <i className="fas fa-eye"></i>
 </Button>
 <Button
 size="sm"
 variant="outline-secondary"
 className="me-1 border-0"
 onClick={() => handlePreview(doc.fileUrl)}
 title="Preview File"
 >
 <i className="fas fa-external-link-alt"></i>
 </Button>
 <Button
 size="sm"
 variant="outline-info"
 className="me-1 border-0"
 onClick={() => handleDownload(doc)}
 title="Download File"
 >
 <i className="fas fa-download"></i>
 </Button>
 <Button
 size="sm"
 variant="outline-danger"
 className="border-0"
 onClick={() => handleDelete(doc)}
 title="Delete Document"
 >
 <i className="fas fa-trash"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* DETAILS MODAL */}
 {selectedDoc && (
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>Document Details</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 <Row className="mb-2">
 <Col md={6}><strong>Type:</strong> {selectedDoc.documentType?.name}</Col>
 <Col md={6}><strong>Status:</strong> <Badge className="rounded-pill ms-2" bg={STATUS_COLORS[selectedDoc.status] || "secondary"}>{selectedDoc.status}</Badge></Col>
 </Row>
 <Row className="mb-2">
 <Col md={6}><strong>File:</strong> {selectedDoc.fileName}</Col>
 <Col md={6}><strong>Version:</strong> v{selectedDoc.version}</Col>
 </Row>
 <Row className="mb-2">
 <Col md={6}><strong>Submitted By:</strong> {selectedDoc.submittedBy?.name}</Col>
 <Col md={6}><strong>Submitted On:</strong> {formatDate(selectedDoc.submissionDate)}</Col>
 </Row>

 <div className="mt-3">
 <Button
 variant="primary"
 onClick={() => handlePreview(selectedDoc.fileUrl)}
 >
 Preview / Download
 </Button>
 </div>
 </Modal.Body>
 </Modal>
 )}
 </>
 );
};

export default DocumentsTab;
